import { inject, observer } from 'mobx-react';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../t';
import { useParams, useSearchParams } from 'react-router-dom';
import Empty from '../../components/Empty';
import http from '../../http';
import { isWeChatBrowser } from '../../util';
import { CustomerImageDialog } from './CustomerImageDialog';
import { CustomerImageGallery } from './CustomerImageGallery';
import { PasswordVrifyDialog } from './PasswordVrifyDialog';
import PaymentDialog from './PaymentDialog';
import { getPaymentStatus, getSelectionStatus } from '../../orderStatus';
import { reportClientMonitorEvent } from '../../clientMonitor';
import { getPictureThumbnailUrl } from '../../pictureUrls';
import SelectionQuotaProgress from './SelectionQuotaProgress';
import { formatMoney, multiplyMoney } from '../../money';
import { getShareAccessError } from './shareAccessErrors';


const C = {
    brand: '#F777B0',
    brand2: '#D94F92',
    ink: '#1A1614',
    ink2: '#3D3632',
    ink3: '#8A8079',
    paper: '#EDE6D5',
    paper2: '#E2D9C6',
    hair: '#E8E0D0',
    hair2: '#D4CABA',
    serif: '"Instrument Serif", "Songti SC", serif',
    sans: '"Inter", "Noto Sans SC", -apple-system, sans-serif',
};

const generateUserId = () => 'h5_' + nanoid(10);
const getUserId = () => {
    let id = localStorage.getItem('customer_user_id');
    if (!id) { id = generateUserId(); localStorage.setItem('customer_user_id', id); }
    return id;
};

function Heart({ filled }) {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M8 13.5C8 13.5 1.5 9.5 1.5 5.5C1.5 3.5 3 2 5 2C6.5 2 7.5 3 8 4C8.5 3 9.5 2 11 2C13 2 14.5 3.5 14.5 5.5C14.5 9.5 8 13.5 8 13.5Z"
                fill={filled ? '#fff' : 'none'}
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function Hourglass() {
    return (
        <svg
            data-testid="wechat-redirect-icon"
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            aria-hidden="true"
        >
            <path d="M10 5h20M10 35h20" stroke={C.ink3} strokeWidth="2.4" strokeLinecap="round" />
            <path
                d="M12.5 6.5c0 6 2.8 9.5 7.5 13.5-4.7 4-7.5 7.5-7.5 13.5m15-27c0 6-2.8 9.5-7.5 13.5 4.7 4 7.5 7.5 7.5 13.5"
                stroke={C.brand2}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path d="M15.5 30.5h9L20 26.8l-4.5 3.7Z" fill={C.brand} />
        </svg>
    );
}

function AlbumLoadingScreen() {
    return (
        <div
            data-testid="customer-album-loading"
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed', inset: 0, zIndex: 1200,
                display: 'grid', placeItems: 'center',
                padding: 24, boxSizing: 'border-box',
                background: C.paper, fontFamily: C.sans,
                WebkitFontSmoothing: 'antialiased',
            }}
        >
            <div style={{ display: 'grid', justifyItems: 'center', textAlign: 'center' }}>
                <div style={{
                    fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
                    color: C.brand2, fontWeight: 600, marginBottom: 22,
                }}>PICSECT · 图派</div>
                <div style={{ position: 'relative', width: 76, height: 68, marginBottom: 24 }} aria-hidden="true">
                    {[0, 1, 2].map(index => (
                        <div key={index} style={{
                            position: 'absolute', width: 44, height: 54,
                            left: 16 + (index - 1) * 8, top: 7 - Math.abs(index - 1) * 2,
                            borderRadius: 6, border: `1px solid ${index === 1 ? C.brand : C.hair2}`,
                            background: index === 1 ? '#fff' : C.paper2,
                            transform: `rotate(${(index - 1) * 9}deg)`,
                            boxShadow: index === 1 ? '0 12px 28px -16px rgba(26,22,20,.45)' : 'none',
                            animation: index === 1 ? 'customerAlbumPulse 1.5s ease-in-out infinite' : 'none',
                        }} />
                    ))}
                </div>
                <div style={{ fontFamily: C.serif, fontSize: 28, color: C.ink, letterSpacing: -0.3 }}>
                    正在加载相册
                </div>
                <div style={{ marginTop: 9, fontSize: 13, lineHeight: 1.6, color: C.ink3 }}>
                    照片较多时，可能需要一点时间
                </div>
            </div>
            <style>{`
                @keyframes customerAlbumPulse {
                    0%, 100% { transform: rotate(0deg) translateY(0); opacity: .72; }
                    50% { transform: rotate(0deg) translateY(-4px); opacity: 1; }
                }
            `}</style>
        </div>
    );
}

// ── Cart Screen ───────────────────────────────────────────────
function CartScreen({ pictures, selectedIds, toggle, order, onBack, onSubmit, selection, albumClaimed, onPay }) {
    const [notes, setNotes] = useState('');
    const limit = order?.photoCount || 0;
    const hasPackage = order?.packageId !== null && order?.packageId !== undefined && order?.packageId !== '';
    const items = selectedIds.map(id => pictures.find(p => p.id === id)).filter(Boolean);
    const overLimit = Math.max(0, items.length - limit);
    const extraCost = multiplyMoney(overLimit, order?.pricePerPic || 30);
    const paymentStatus = getPaymentStatus(selection);
    const canPay = order?.isOnlinePayment && !order?.forceLimit && albumClaimed && ['unpaid', 'pending', 'partial'].includes(paymentStatus) && selection?.totalPrice > 0;
    const isPaid = albumClaimed && paymentStatus === 'paid';

    const Row = ({ label, value, accent }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, gap: 12 }}>
            <span style={{ color: C.ink3 }}>{label}</span>
            <span style={{ color: accent ? C.brand2 : C.ink, fontVariantNumeric: 'tabular-nums', fontWeight: accent ? 500 : 400, whiteSpace: 'nowrap' }}>{value}</span>
        </div>
    );

    return (
        <div className="customer-cart-screen" data-testid="customer-cart-screen" style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: C.paper,
            display: 'flex', flexDirection: 'column',
            fontFamily: C.sans,
            WebkitFontSmoothing: 'antialiased',
        }}>
            {/* header */}
            <div className="customer-cart-header" style={{
                padding: '60px 18px 14px',
                borderBottom: `1px solid ${C.hair}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
            }}>
                <button data-testid="customer-cart-back" onClick={onBack} style={{
                    width: 36, height: 36, borderRadius: 18, border: 'none',
                    background: 'transparent', color: C.ink,
                    fontSize: 22, cursor: 'pointer',
                    display: 'grid', placeItems: 'center',
                }}>‹</button>
                <div style={{ fontFamily: C.serif, fontSize: 18, color: C.ink }}>
                    {albumClaimed ? '选片清单' : '确认选片'}
                </div>
                <div style={{ width: 36 }} />
            </div>

            <div className="customer-cart-content" data-testid="customer-cart-content" style={{ flex: 1, overflow: 'auto', padding: '20px 18px 24px' }}>
                <div className="customer-cart-sidebar">
                    {/* summary card */}
                    <div style={{
                        padding: 18, borderRadius: 12,
                        background: '#fff', border: `1px solid ${C.hair}`,
                        marginBottom: 18,
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                            <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.ink3 }}>已选</div>
                            <div style={{ fontFamily: C.serif, fontSize: 28, color: C.ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {items.length}<span style={{ color: C.ink3, fontSize: 18 }}> / {limit}</span>
                            </div>
                        </div>
                        <div style={{ height: 1, background: C.hair, margin: '14px 0' }} />
                        <Row label={hasPackage ? '套餐内' : '可选数量内'} value={`${Math.min(items.length, limit)} 张`} />
                        <Row label="加片" value={`${overLimit} 张 × ¥${formatMoney(order?.pricePerPic || 30)}`} accent={overLimit > 0} />
                        <div style={{ height: 1, background: C.hair, margin: '12px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <div style={{ fontSize: 13, color: C.ink2 }}>需补差额</div>
                            <div style={{ fontFamily: C.serif, fontSize: 24, color: extraCost ? C.brand2 : C.ink3 }}>
                                ¥{formatMoney(extraCost)}
                            </div>
                        </div>
                    </div>

                    {/* notes */}
                    {!albumClaimed && (
                        <div style={{
                            padding: 16, borderRadius: 10,
                            background: '#fff', border: `1px solid ${C.hair}`,
                            marginBottom: 18,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                                    给摄影师的备注
                                </div>
                                <span style={{
                                    padding: '2px 7px', borderRadius: 10,
                                    background: C.paper2, color: C.ink3,
                                    fontSize: 10.5, fontWeight: 500,
                                }}>可选</span>
                            </div>
                            <div style={{ fontSize: 12, color: C.ink3, lineHeight: 1.5, marginBottom: 10 }}>
                                有特殊修图或选片要求，可以写在这里。
                            </div>
                            <textarea
                                className="customer-selection-note"
                                data-testid="customer-selection-note"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="填写备注…"
                                style={{
                                    width: '100%', minHeight: 72, resize: 'vertical',
                                    border: `1px solid ${C.hair}`, outline: 'none',
                                    borderRadius: 8, padding: '10px 12px',
                                    fontFamily: C.sans, fontSize: 13.5, color: C.ink,
                                    background: C.paper, lineHeight: 1.5, boxSizing: 'border-box',
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="customer-cart-gallery">
                    <div style={{ fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.ink3, marginBottom: 10 }}>
                        {albumClaimed ? '选片清单' : '已选照片'} · {items.length}
                    </div>
                    {items.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: C.ink3, fontSize: 13 }}>
                            还没有选片
                        </div>
                    ) : (
                        <div className="customer-cart-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                            {items.map(p => (
                                <div key={p.id} style={{
                                    position: 'relative', aspectRatio: '4/5',
                                    borderRadius: 4, overflow: 'hidden',
                                    background: 'linear-gradient(155deg, #d8a87a, #5a3a2a)',
                                }}>
                                    {getPictureThumbnailUrl(p) && (
                                        <img
                                            data-testid={`customer-cart-image-${p.id}`}
                                            src={getPictureThumbnailUrl(p)}
                                            alt={p.fileName || ''}
                                            decoding="async"
                                            style={{
                                                position: 'absolute', inset: 0,
                                                width: '100%', height: '100%',
                                                objectFit: 'cover', objectPosition: 'center',
                                                display: 'block',
                                            }}
                                        />
                                    )}
                                    {!albumClaimed && (
                                        <button
                                            data-testid={`customer-cart-remove-${p.id}`}
                                            onClick={() => toggle(p.id)}
                                            style={{
                                                position: 'absolute', top: 4, right: 4,
                                                width: 20, height: 20, borderRadius: 10, border: 'none',
                                                background: 'rgba(0,0,0,.5)', color: '#fff',
                                                fontSize: 11, cursor: 'pointer',
                                                backdropFilter: 'blur(6px)',
                                                WebkitBackdropFilter: 'blur(6px)',
                                                display: 'grid', placeItems: 'center',
                                                zIndex: 2,
                                            }}>×</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* sticky submit */}
            <div className="customer-cart-footer" style={{
                padding: '14px 18px 20px',
                borderTop: `1px solid ${C.hair}`,
                background: 'rgba(237,230,213,.95)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                flexShrink: 0,
            }}>
                {canPay && (
                    <button
                        onClick={onPay}
                        style={{
                            width: '100%', padding: 16, marginBottom: 10,
                            background: C.brand, color: '#fff', border: 'none', borderRadius: 12,
                            fontFamily: C.sans, fontSize: 15, fontWeight: 500,
                            cursor: 'pointer',
                        }}>
                        支付 ¥{formatMoney(selection.totalPrice)}
                    </button>
                )}
                {isPaid && (
                    <div style={{
                        padding: '14px 16px', borderRadius: 12,
                        background: C.paper2, border: `1px solid ${C.hair}`,
                        color: C.ink3, fontSize: 13, textAlign: 'center', marginBottom: 10,
                    }}>✓ 已完成支付</div>
                )}
                {!albumClaimed && (
                    <button
                        data-testid="customer-submit-selection"
                        onClick={() => onSubmit(notes)}
                        disabled={items.length === 0}
                        style={{
                            width: '100%', padding: 16,
                            background: items.length === 0 ? C.hair2 : C.ink,
                            color: '#fff', border: 'none', borderRadius: 12,
                            fontFamily: C.sans, fontSize: 15, fontWeight: 500,
                            cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        }}>
                        {extraCost > 0 ? `提交并支付 ¥${formatMoney(extraCost)}` : '提交选片'}
                        <span style={{ opacity: 0.6 }}>→</span>
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Done Screen ───────────────────────────────────────────────
function DoneScreen({ count, extraCost, order, selection, onReselect, onViewSelection, onPay }) {
    const paymentStatus = getPaymentStatus(selection);
    const rawPaymentStatus = selection?.rawPaymentStatus || selection?.paymentStatus;
    const canPay = order?.isOnlinePayment && !order?.forceLimit && ['unpaid', 'pending', 'partial'].includes(paymentStatus) && selection?.totalPrice > 0;
    const isPaid = paymentStatus === 'paid';
    const canReselect = !isPaid && rawPaymentStatus !== 'requested';
    return (
        <div className="customer-done-screen" data-testid="customer-done-screen" style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: C.paper,
            display: 'grid', placeItems: 'center',
            fontFamily: C.sans,
            WebkitFontSmoothing: 'antialiased',
            padding: '32px 20px',
            textAlign: 'center',
            overflowY: 'auto',
        }}>
            <div className="customer-done-content" style={{ width: '100%', maxWidth: 420 }}>
                <div style={{
                    width: 64, height: 64, borderRadius: 32,
                    background: C.brand,
                    display: 'grid', placeItems: 'center',
                    color: '#fff', fontSize: 29, fontFamily: C.serif,
                    boxShadow: '0 14px 32px -14px rgba(247,119,176,.55)',
                    margin: '0 auto 18px',
                }}>✓</div>

                <div style={{
                    fontFamily: 'ui-monospace, monospace', fontSize: 10,
                    letterSpacing: 1.6, color: C.brand, fontWeight: 600,
                    marginBottom: 7,
                }}>SUBMITTED</div>

                <h1 style={{
                    fontFamily: C.serif, fontWeight: 400,
                    fontSize: 34, margin: '0 0 7px', color: C.ink,
                }}>已发送给摄影师</h1>
                <div style={{ fontSize: 13, color: C.ink3, marginBottom: 22 }}>
                    摄影师已收到你的选片结果
                </div>

                <div data-testid="customer-done-summary" style={{
                    display: 'flex', alignItems: 'stretch',
                    background: '#fff', border: `1px solid ${C.hair}`,
                    borderRadius: 12, overflow: 'hidden', marginBottom: 14,
                }}>
                    <div style={{ flex: 1, padding: '15px 12px' }}>
                        <div style={{ fontFamily: C.serif, fontSize: 24, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
                            {count}
                        </div>
                        <div style={{ marginTop: 2, fontSize: 11, color: C.ink3 }}>共 {count} 张</div>
                    </div>
                    {extraCost > 0 && (
                        <>
                            <div style={{ width: 1, background: C.hair }} />
                            <div style={{ flex: 1, padding: '15px 12px' }}>
                                <div style={{ fontFamily: C.serif, fontSize: 24, color: C.brand2, fontVariantNumeric: 'tabular-nums' }}>
                                    ¥{formatMoney(extraCost)}
                                </div>
                                <div style={{ marginTop: 2, fontSize: 11, color: C.ink3 }}>加片金额</div>
                            </div>
                        </>
                    )}
                </div>

                {canPay && (
                    <button
                        onClick={onPay}
                        style={{
                            width: '100%', padding: 15, marginBottom: 10,
                            background: C.brand, color: '#fff', border: 'none', borderRadius: 12,
                            fontFamily: C.sans, fontSize: 15, fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 8px 24px -8px rgba(247,119,176,.5)',
                        }}>
                        支付 ¥{formatMoney(selection.totalPrice)}
                    </button>
                )}

                {isPaid && (
                    <div style={{
                        marginBottom: 10, padding: '12px 16px', borderRadius: 10,
                        background: C.paper2, border: `1px solid ${C.hair}`,
                        color: C.ink2, fontSize: 13,
                    }}>✓ 已完成支付</div>
                )}

                <button data-testid="customer-view-selection-list" onClick={onViewSelection} style={{
                    width: '100%', padding: '13px 16px',
                    border: `1.5px solid ${C.hair2}`,
                    background: '#fff', color: C.ink2, borderRadius: 10,
                    fontFamily: C.sans, fontSize: 14, fontWeight: 500,
                    cursor: 'pointer',
                }}>查看选片清单</button>

                {canReselect && (
                    <button data-testid="customer-reselect" onClick={onReselect} style={{
                        marginTop: 12, padding: '8px 16px', border: 'none', background: 'transparent',
                        color: C.ink3, fontFamily: C.sans, fontSize: 12.5,
                        cursor: 'pointer',
                    }}>重新选片</button>
                )}
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────
const OrderSelect = inject('snackbarStore', 'anonymousUserStore')(observer(({ anonymousUserStore, snackbarStore }) => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const pwdFromParam = searchParams.get('pwd');

    const [order, setOrder] = useState(null);
    const [needpassword, setNeedpassword] = useState(!anonymousUserStore.accessToken);
    const [pictures, setPictures] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerIdx, setViewerIdx] = useState(0);
    const [albumClaimed, setAlbumClaimed] = useState(false);
    const [selection, setSelection] = useState(null);
    const [screen, setScreen] = useState('gallery'); // 'gallery' | 'cart' | 'done'
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [paymentQrCode, setPaymentQrCode] = useState(null);
    const [pwd, setPwd] = useState(pwdFromParam || '');
    const [pageError, setPageError] = useState(null);
    const [tagDefinitions, setTagDefinitions] = useState([]);
    const [customerTagAssignments, setCustomerTagAssignments] = useState({});
    const [galleryFilter, setGalleryFilter] = useState('all');
    const [personGroups, setPersonGroups] = useState([]);
    const [personFilter, setPersonFilter] = useState('all');
    const [multiMode, setMultiMode] = useState(false);
    const [miniProgramRedirectError, setMiniProgramRedirectError] = useState(false);
    const [miniProgramRedirectAttempt, setMiniProgramRedirectAttempt] = useState(0);
    const [albumLoading, setAlbumLoading] = useState(Boolean(anonymousUserStore.accessToken));
    const miniProgramRedirectStartedRef = useRef(false);
    const hasLoadedAlbumRef = useRef(false);

    const { t } = useTranslation();
    const weChatBrowser = useMemo(() => isWeChatBrowser(), []);

    const limit = order?.photoCount || 0;
    const hasPackage = order?.packageId !== null && order?.packageId !== undefined && order?.packageId !== '';
    const overLimit = Math.max(0, selectedIds.length - limit);
    const extraCost = multiplyMoney(overLimit, order?.pricePerPic || 30);
    const filteredPictures = useMemo(() => {
        const group = personFilter === 'all' ? null : personGroups.find(item => item.id === personFilter);
        const groupPictureIds = group ? new Set(group.pictureIds.map(String)) : null;
        return pictures.filter(picture => {
            if (galleryFilter === 'selected' && !selectedIds.includes(picture.id)) return false;
            if (galleryFilter === 'unselected' && selectedIds.includes(picture.id)) return false;
            if (groupPictureIds && !groupPictureIds.has(String(picture.id))) return false;
            return true;
        });
    }, [pictures, selectedIds, galleryFilter, personGroups, personFilter]);

    const fetchOrderData = useCallback((submissionVerification = null) => {
        if (!anonymousUserStore.accessToken) return;
        if (!hasLoadedAlbumRef.current) setAlbumLoading(true);
        const userId = getUserId();
        http.get(`/customer/order/${orderId}/select?userId=${userId}`, {
            headers: { 'x-access-token': anonymousUserStore.accessToken }
        }).then(response => {
            setNeedpassword(false);
            const pics = response.data?.pictures || [];
            setPictures(pics);

            const local = window.localStorage.getItem(`pictrue_selection_${orderId}`);
            if (local) {
                setSelectedIds(JSON.parse(local));
            } else {
                setSelectedIds(pics.filter(p => p.claimed).map(p => p.id));
            }

            const ord = response.data?.order;
            setOrder(ord);
            if (ord?.Album?.title) document.title = ord.Album.title;
            setPwd(ord?.Album?.password || '');
            setTagDefinitions(ord?.tagDefinitions || []);
            setCustomerTagAssignments(ord?.customerTagAssignments || {});
            const savedPersonGroups = response.data?.personGroups;
            setPersonGroups(savedPersonGroups?.customerVisible === true ? (savedPersonGroups.groups || []) : []);
            setPersonFilter('all');

            const sel = response.data?.selection;
            const restoredSelectedIds = sel?.pictureIds
                || (local ? JSON.parse(local) : pics.filter(p => p.claimed).map(p => p.id));
            reportClientMonitorEvent('selection_album_loaded', {
                orderRef: orderId,
                selectionRef: sel?.id,
                photoTotal: pics.length,
                selectedCount: restoredSelectedIds.length,
                existingSelection: Boolean(sel),
            });
            if (submissionVerification) {
                const expectedIds = submissionVerification.selectedPictureIds.map(String).sort();
                const persistedIds = Array.isArray(sel?.pictureIds)
                    ? sel.pictureIds.map(String).sort()
                    : null;
                const persistedMatches = Boolean(persistedIds)
                    && expectedIds.length === persistedIds.length
                    && expectedIds.every((id, index) => id === persistedIds[index]);
                reportClientMonitorEvent(
                    persistedMatches ? 'selection_submit_verified' : 'selection_submit_response_mismatch',
                    {
                        level: persistedMatches ? 'info' : 'error',
                        orderRef: orderId,
                        selectionRef: sel?.id || submissionVerification.selectionId,
                        selectedCount: expectedIds.length,
                        persistedCount: persistedIds?.length ?? -1,
                        extraCount: submissionVerification.extraCount,
                        updateExisting: submissionVerification.updateExisting,
                        immediate: true,
                    }
                );
            }
            if (sel) {
                const selectionStatus = getSelectionStatus(sel);
                const hasSelectionStatus = ['draft', 'selecting', 'submitted', 'expired'].includes(selectionStatus);
                const isSubmitted = hasSelectionStatus ? selectionStatus === 'submitted' : true;
                setAlbumClaimed(isSubmitted);
                setSelection(sel);
                setSelectedIds(sel.pictureIds);
                setScreen(isSubmitted ? 'done' : 'gallery');
            } else {
                setAlbumClaimed(false);
                setSelection(null);
                setScreen('gallery');
            }
            hasLoadedAlbumRef.current = true;
            setAlbumLoading(false);
        }).catch(error => {
            const accessError = getShareAccessError(error);
            if (accessError.status === 401) {
                reportClientMonitorEvent('selection_gallery_access_required', {
                    level: 'info', orderRef: orderId, httpStatus: 401,
                });
            } else if (accessError.terminal) {
                reportClientMonitorEvent('selection_gallery_access_unavailable', {
                    level: 'warn', orderRef: orderId,
                    httpStatus: accessError.status,
                    failureKind: accessError.code || 'share_unavailable',
                });
            } else {
                reportClientMonitorEvent('selection_gallery_load_failed', {
                    level: 'warn', orderRef: orderId, httpStatus: accessError.status,
                });
            }
            anonymousUserStore.empty();
            setAlbumLoading(false);
            setNeedpassword(true);
        });
    }, [orderId, anonymousUserStore]);

    const handlePictureSelect = useCallback((id, claimed) => {
        setSelectedIds(currentIds => {
            const alreadySelected = currentIds.includes(id);
            if (claimed === alreadySelected) return currentIds;
            if (order?.forceLimit && claimed && currentIds.length >= limit) {
                reportClientMonitorEvent('selection_limit_reached', {
                    level: 'warn', orderRef: orderId,
                    selectedCount: currentIds.length, selectionLimit: limit,
                });
                setPageError(t('Picture selection limit reached.'));
                return currentIds;
            }
            const next = claimed
                ? [...currentIds, id]
                : currentIds.filter(x => x !== id);
            window.localStorage.setItem(`pictrue_selection_${orderId}`, JSON.stringify(next));
            return next;
        });
    }, [order, limit, orderId, t]);

    const toggle = useCallback((id) => {
        const claimed = !selectedIds.includes(id);
        handlePictureSelect(id, claimed);
    }, [selectedIds, handlePictureSelect]);

    const handleTagToggle = useCallback((photoId, tagId) => {
        const current = customerTagAssignments[photoId] || [];
        const next = current.includes(tagId) ? current.filter(id => id !== tagId) : [...current, tagId];
        setCustomerTagAssignments(prev => ({ ...prev, [photoId]: next }));
        http.put(`/customer/order/${orderId}/photo/${photoId}/tags`, { tagIds: next }, {
            headers: { 'x-access-token': anonymousUserStore.accessToken }
        }).then(response => {
            const saved = response.data?.tagIds || next;
            setCustomerTagAssignments(prev => ({ ...prev, [photoId]: saved }));
        }).catch(() => {
            setCustomerTagAssignments(prev => ({ ...prev, [photoId]: current }));
            snackbarStore.showSnackbar('照片标记保存失败', 'error');
        });
    }, [customerTagAssignments, orderId, anonymousUserStore.accessToken, snackbarStore]);

    const claimAlbum = useCallback((notes = '') => {
        const userId = getUserId();
        const data = { selectedPictureIds: selectedIds, selectCustomerName: userId, userId, notes };
        const method = selection ? http.put : http.post;
        reportClientMonitorEvent('selection_submit_started', {
            orderRef: orderId,
            selectionRef: selection?.id,
            selectedCount: selectedIds.length,
            extraCount: Math.max(0, selectedIds.length - limit),
            updateExisting: Boolean(selection),
            immediate: true,
        });
        method(`/customer/order/${orderId}/claim`, data, {
            headers: { 'x-access-token': anonymousUserStore.accessToken }
        }).then(response => {
            const selectionId = response.data?.selectionId || selection?.id;
            const extraCount = Math.max(0, selectedIds.length - limit);
            reportClientMonitorEvent('selection_submit_succeeded', {
                orderRef: orderId,
                selectionRef: selectionId,
                selectedCount: selectedIds.length,
                extraCount,
                updateExisting: Boolean(selection),
                immediate: true,
            });
            fetchOrderData({
                selectionId,
                selectedPictureIds: selectedIds,
                extraCount,
                updateExisting: Boolean(selection),
            });
        }).catch(error => {
            const accessError = getShareAccessError(error);
            reportClientMonitorEvent('selection_submit_failed', {
                level: 'error', orderRef: orderId, selectionRef: selection?.id,
                selectedCount: selectedIds.length,
                httpStatus: error.response?.status || 0,
                updateExisting: Boolean(selection),
                immediate: true,
            });
            setPageError(accessError.terminal ? accessError.message : t('Submit failed.'));
        });
    }, [selectedIds, selection, orderId, limit, anonymousUserStore.accessToken, fetchOrderData, t]);

    const openSelectionReview = useCallback(() => {
        reportClientMonitorEvent('selection_review_opened', {
            orderRef: orderId,
            selectionRef: selection?.id,
            selectedCount: selectedIds.length,
            extraCount: Math.max(0, selectedIds.length - limit),
        });
        setScreen('cart');
    }, [orderId, selection, selectedIds.length, limit]);

    const payAlbum = useCallback(() => {
        http.get(`/customer/order/${orderId}/qrCode?pwd=${pwd}&selectionId=${selection?.id}`, {
            headers: { 'x-access-token': anonymousUserStore.accessToken }
        }).then(response => {
            setShowPaymentDialog(true);
            setPaymentQrCode(response.data.imageData);
        }).catch(() => setPageError(t('Payment failed.')));
    }, [orderId, pwd, selection, anonymousUserStore.accessToken, snackbarStore, t]);

    const checkIsPayed = useCallback(() => {
        http.get(`/customer/selection/${selection?.id}/isPaid`, {
            headers: { 'x-access-token': anonymousUserStore.accessToken }
        }).then(response => {
            if (response.data.paymentStatus === 'paid') {
                setPageError(null);
                setShowPaymentDialog(false);
                fetchOrderData();
            } else {
                setPageError(t('Payment not completed yet.'));
            }
        }).catch(() => setPageError(t('Error checking payment status.')));
    }, [anonymousUserStore.accessToken, selection, fetchOrderData, snackbarStore, t]);

    const handleAccessVerified = useCallback((verifiedPassword) => {
        setPwd(verifiedPassword);
        setAlbumLoading(true);
        setNeedpassword(false);
    }, []);

    useEffect(() => {
        if (!weChatBrowser || !orderId) return;
        if (miniProgramRedirectStartedRef.current) return;
        miniProgramRedirectStartedRef.current = true;
        setMiniProgramRedirectError(false);
        http.get(`/customer/order/${orderId}/miniprogramUrl?pwd=${encodeURIComponent(pwdFromParam || '')}`).then(response => {
            const url = response.data?.url;
            if (!url) throw new Error('Missing mini-program URL');
            window.location.assign(url);
        }).catch(() => {
            miniProgramRedirectStartedRef.current = false;
            setMiniProgramRedirectError(true);
        });
        const local = window.localStorage.getItem(`pictrue_selection_${orderId}`);
        if (local) setSelectedIds(JSON.parse(local));
    }, [weChatBrowser, orderId, pwdFromParam, miniProgramRedirectAttempt]);

    useEffect(() => {
        if (!needpassword && !weChatBrowser) {
            anonymousUserStore.check();
            fetchOrderData();
        }
    }, [fetchOrderData, anonymousUserStore, needpassword, weChatBrowser]);

    if (weChatBrowser) {
        return (
            <Empty message={miniProgramRedirectError ? '暂时无法打开小程序' : t('Redirect to miniprogram to select pictures.')}>
                <div style={{ display: 'grid', justifyItems: 'center', gap: 16 }}>
                    <Hourglass />
                    {miniProgramRedirectError && (
                        <button
                            type="button"
                            data-testid="wechat-redirect-retry"
                            onClick={() => setMiniProgramRedirectAttempt(attempt => attempt + 1)}
                            style={{
                                border: 'none', borderRadius: 10, padding: '11px 18px',
                                background: C.ink, color: '#fff', fontFamily: C.sans,
                                fontSize: 14, cursor: 'pointer',
                            }}
                        >重新尝试</button>
                    )}
                </div>
            </Empty>
        );
    }

    if (needpassword) {
        return (
            <PasswordVrifyDialog
                open={needpassword}
                pwd={pwdFromParam}
                orderId={orderId}
                customerUserId={getUserId()}
                onVerified={handleAccessVerified}
                anonymousUserStore={anonymousUserStore}
            />
        );
    }

    if (albumLoading || !order) {
        return <AlbumLoadingScreen />;
    }

    const title = order?.Album?.title || order?.customerName || '';
    const density = 3;

    return (
        <div style={{
            display: 'grid', placeItems: 'center', width: '100%', maxWidth: '100vw',
            minHeight: '100vh', overflow: 'hidden', background: C.paper,
        }}>
        <div className="customer-select-shell" data-testid="customer-select-shell" style={{
            position: 'relative', width: '100%', maxWidth: '100vw', minWidth: 0,
            minHeight: '100vh', height: '100vh', overflow: 'hidden', boxSizing: 'border-box',
            background: C.paper, fontFamily: C.sans,
            WebkitFontSmoothing: 'antialiased',
        }}>
            {/* Error banner */}
            {pageError && (
                <div onClick={() => setPageError(null)} style={{
                    position: 'absolute', top: 60, left: 12, right: 12, zIndex: 300,
                    padding: '12px 14px', borderRadius: 10,
                    background: '#FBE0E0', color: '#8C2020',
                    border: '1px solid #F0C0C0',
                    fontSize: 13, fontFamily: C.sans, cursor: 'pointer', textAlign: 'center',
                }}>
                    {pageError}
                </div>
            )}
            {/* ── Gallery Screen ── */}
            <div style={{
                display: 'flex', flexDirection: 'column',
                height: '100%', overflow: 'hidden',
                background: C.paper,
            }}>
                {/* sticky header */}
                <div className="customer-main-header" style={{
                    padding: '54px 18px 14px',
                    background: 'rgba(237,230,213,.92)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: `1px solid ${C.hair}`,
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, letterSpacing: 1.4, color: C.brand, fontWeight: 600, textTransform: 'uppercase' }}>
                                PICSECT · 选片
                            </div>
                            <div style={{ fontFamily: C.serif, fontSize: 26, lineHeight: 1.05, marginTop: 5, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {title}
                            </div>
                        </div>
                        <div data-testid="customer-selection-counter" style={{ flexShrink: 0, textAlign: 'right' }}>
                            <div style={{ fontFamily: C.serif, fontSize: 30, lineHeight: 1, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
                                {selectedIds.length}<span style={{ color: C.ink3, fontSize: 17 }}>/{limit}</span>
                            </div>
                            <div style={{ marginTop: 4, fontFamily: 'ui-monospace, monospace', fontSize: 9, color: C.ink3, letterSpacing: 1.4 }}>
                                SELECTED
                            </div>
                        </div>
                    </div>

                    <SelectionQuotaProgress
                        selectedCount={selectedIds.length}
                        selectionLimit={limit}
                        hasPackage={hasPackage}
                        forceLimit={Boolean(order?.forceLimit)}
                        pricePerPic={order?.pricePerPic || 30}
                    />
                </div>

                <div className="customer-filter-bar" style={{
                    display: 'flex', alignItems: 'center', gap: 24,
                    padding: '9px 16px 8px',
                    borderBottom: `1px solid ${C.hair}`,
                    background: C.paper, flexShrink: 0,
                }}>
                    {[
                        { value: 'all', label: '全部', count: pictures.length },
                        { value: 'selected', label: '已选', count: selectedIds.length },
                        { value: 'unselected', label: '未选', count: pictures.length - selectedIds.length },
                    ].map(tab => (
                        <button
                            key={tab.value}
                            type="button"
                            data-testid={`customer-filter-${tab.value}`}
                            aria-pressed={galleryFilter === tab.value}
                            onClick={() => setGalleryFilter(tab.value)}
                            style={{
                                padding: '6px 0 7px', border: 'none',
                                borderBottom: `2px solid ${galleryFilter === tab.value ? C.ink : 'transparent'}`,
                                background: 'transparent',
                                color: galleryFilter === tab.value ? C.ink : C.ink3,
                                fontFamily: C.sans, fontSize: 13,
                                fontWeight: galleryFilter === tab.value ? 600 : 400,
                                cursor: 'pointer',
                            }}
                        >{tab.label} <span style={{ fontSize: 11, opacity: .7 }}>{tab.count}</span></button>
                    ))}
                </div>

                {personGroups.length > 0 && (
                    <div data-testid="customer-person-filters" style={{
                        display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto',
                        padding: '9px 16px', borderBottom: `1px solid ${C.hair}`,
                        background: '#F7F1E6', flexShrink: 0,
                    }}>
                        <button type="button" data-testid="customer-person-filter-all" onClick={() => setPersonFilter('all')} style={{
                            minHeight: 34, padding: '0 12px', borderRadius: 18,
                            border: `1px solid ${personFilter === 'all' ? C.ink : C.hair2}`,
                            background: personFilter === 'all' ? C.ink : '#fff',
                            color: personFilter === 'all' ? '#fff' : C.ink2,
                            fontFamily: C.sans, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>全部人物</button>
                        {personGroups.map(group => {
                            const representative = pictures.find(picture => String(picture.id) === String(group.representative?.pictureId));
                            const bounds = group.representative?.bounds;
                            const centerX = bounds ? ((bounds.left + bounds.right) / 2) * 100 : 50;
                            const centerY = bounds ? ((bounds.top + bounds.bottom) / 2) * 100 : 50;
                            const active = personFilter === group.id;
                            return (
                                <button key={group.id} type="button" data-testid={`customer-person-filter-${group.id}`} onClick={() => setPersonFilter(group.id)} style={{
                                    minHeight: 34, padding: '2px 11px 2px 3px', borderRadius: 18,
                                    border: `1px solid ${active ? C.ink : C.hair2}`,
                                    background: active ? C.ink : '#fff', color: active ? '#fff' : C.ink2,
                                    display: 'inline-flex', alignItems: 'center', gap: 7,
                                    fontFamily: C.sans, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                                }}>
                                    <span style={{ width: 28, height: 28, borderRadius: 14, overflow: 'hidden', background: C.paper2, display: 'inline-flex' }}>
                                        {representative && (
                                            <img src={representative.publicUrlThumbnail || representative.publicUrlSmall || representative.publicUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${centerX}% ${centerY}%` }}/>
                                        )}
                                    </span>
                                    <span>{group.label} · {group.photoCount}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* photo grid */}
                <div className="customer-gallery-scroll" style={{
                    flex: 1, width: '100%', minWidth: 0, overflow: 'auto',
                    padding: 4, boxSizing: 'border-box',
                }}>
                    {filteredPictures.length === 0 ? (
                        <div style={{ padding: '60px 0', textAlign: 'center', color: C.ink3, fontSize: 14 }}>
                            {pictures.length === 0 ? t('No picture available.') : '这里暂时没有照片'}
                        </div>
                    ) : (
                        <div className="customer-photo-grid" style={{
                            display: 'grid', width: '100%', minWidth: 0,
                            gridTemplateColumns: `repeat(${density}, minmax(0, 1fr))`,
                            gap: 4,
                        }}>
                            {filteredPictures.map(p => (
                                <CustomerImageGallery
                                    key={p.id}
                                    picture={p}
                                    isSelected={selectedIds.includes(p.id)}
                                    albumClaimed={albumClaimed}
                                    showImageName={order?.showImageName}
                                    watermark={false}
                                    cols={density}
                                    multiMode={multiMode}
                                    handlePictureSelect={handlePictureSelect}
                                    handlePreview={() => {
                                        setViewerIdx(pictures.findIndex(item => item.id === p.id));
                                        setViewerOpen(true);
                                    }}
                                    tagDefinitions={tagDefinitions}
                                    appliedTagIds={customerTagAssignments[p.id] || []}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="customer-selection-bottom-bar" data-testid="customer-selection-bottom-bar" style={{
                    width: '100%', minWidth: 0, minHeight: 76, padding: '11px 16px 15px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    background: 'rgba(237,230,213,.96)',
                    boxSizing: 'border-box', overflow: 'hidden',
                    borderTop: `1px solid ${C.hair}`,
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    flexShrink: 0, zIndex: 20,
                }}>
                    <button
                        type="button"
                        data-testid="customer-multi-mode-toggle"
                        aria-pressed={multiMode}
                        onClick={() => setMultiMode(value => !value)}
                        style={{
                            width: 52, height: 52, borderRadius: 26,
                            border: `1.5px solid ${C.hair2}`, background: '#fff',
                            color: C.ink2, fontFamily: C.sans, fontSize: 12, fontWeight: 600,
                            cursor: 'pointer', flexShrink: 0,
                        }}>
                        {multiMode ? '完成' : '多选'}
                    </button>
                    <button
                        type="button"
                        data-testid="customer-review-selection"
                        aria-label={selectedIds.length > 0 ? '确认选片 →' : '请先选择照片'}
                        disabled={selectedIds.length === 0}
                        onClick={openSelectionReview}
                        style={{
                            flex: '1 1 auto', width: 'auto', minWidth: 0, maxWidth: 456, height: 52,
                            padding: '0 14px', borderRadius: 26, border: 'none',
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: selectedIds.length === 0 ? 'rgba(26,22,20,.42)' : (overLimit > 0 ? C.brand : C.ink),
                            color: '#fff', fontFamily: C.sans, fontSize: 14, fontWeight: 600,
                            cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer',
                            boxShadow: selectedIds.length === 0 ? 'none' : '0 12px 26px -18px rgba(26,22,20,.55)',
                        }}>
                        <span style={{
                            width: 34, height: 34, borderRadius: 17,
                            background: selectedIds.length === 0 ? 'rgba(247,119,176,.25)' : (overLimit > 0 ? '#fff' : C.brand),
                            color: overLimit > 0 ? C.brand2 : '#fff',
                            display: 'grid', placeItems: 'center',
                            fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                        }}>{selectedIds.length}</span>
                        <span style={{ flex: 1, minWidth: 0, textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {selectedIds.length === 0 ? '请先选择照片' : (overLimit > 0 ? '确认并支付' : '确认选片')}
                        </span>
                        {overLimit > 0 && <span>¥{formatMoney(extraCost)}</span>}
                        <span style={{ opacity: .65 }}>→</span>
                    </button>
                </div>
            </div>

            {/* ── Viewer overlay ── */}
            {viewerOpen && (
                <CustomerImageDialog
                    open={viewerOpen}
                    setOpen={setViewerOpen}
                    currentIndex={viewerIdx}
                    setCurrentIndex={setViewerIdx}
                    previewPictures={pictures}
                    selectedPicturesIds={selectedIds}
                    handlePictureSelect={handlePictureSelect}
                    photoCount={limit}
                    albumClaimed={albumClaimed}
                    watermark={false}
                    tagDefinitions={tagDefinitions}
                    customerTagAssignments={customerTagAssignments}
                    onTagToggle={handleTagToggle}
                />
            )}

            {/* ── Cart overlay ── */}
            {screen === 'cart' && (
                <CartScreen
                    pictures={pictures}
                    selectedIds={selectedIds}
                    toggle={toggle}
                    order={order}
                    onBack={() => setScreen(albumClaimed ? 'done' : 'gallery')}
                    onSubmit={claimAlbum}
                    selection={selection}
                    albumClaimed={albumClaimed}
                    onPay={payAlbum}
                />
            )}

            {/* ── Done overlay ── */}
            {screen === 'done' && (
                <DoneScreen
                    count={selectedIds.length}
                    extraCost={extraCost}
                    order={order}
                    selection={selection}
                    onViewSelection={openSelectionReview}
                    onReselect={() => {
                        reportClientMonitorEvent('selection_reselect_started', {
                            orderRef: orderId,
                            selectionRef: selection?.id,
                            selectedCount: selectedIds.length,
                        });
                        setAlbumClaimed(false);
                        setSelection(null);
                        setScreen('gallery');
                    }}
                    onPay={payAlbum}
                />
            )}

            <PaymentDialog
                open={showPaymentDialog}
                paymentQrCode={paymentQrCode}
                onClose={() => setShowPaymentDialog(false)}
                onPaymentCompleted={checkIsPayed}
            />
            <style>{`
                .customer-select-shell { max-width: none; }
                .customer-selection-note::placeholder {
                    color: #B8AFA7;
                    opacity: 1;
                }
                .customer-selection-note:focus {
                    border-color: ${C.brand} !important;
                }
                @media (max-width: 479px) {
                    .customer-cart-grid { grid-template-columns: repeat(3, 1fr) !important; }
                    .customer-done-screen { padding: 64px 18px 18px !important; }
                }
                @media (min-width: 720px) {
                    .customer-select-shell {
                        max-width: none;
                    }
                    .customer-main-header { padding: 28px 28px 14px !important; }
                    .customer-filter-bar { padding: 9px 28px 8px !important; }
                    .customer-gallery-scroll { padding: 8px !important; }
                    .customer-selection-bottom-bar { padding: 11px 28px 13px !important; }
                    .customer-photo-grid { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 6px !important; }
                    .customer-cart-screen {
                        width: 100% !important;
                    }
                    .customer-cart-header { padding: 18px 24px 14px !important; }
                    .customer-cart-content {
                        display: grid;
                        grid-template-columns: 280px minmax(0, 1fr);
                        align-items: start;
                        gap: 24px;
                        padding: 24px !important;
                    }
                    .customer-cart-sidebar { position: sticky; top: 0; }
                    .customer-cart-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 8px !important; }
                    .customer-cart-footer { padding: 16px 24px 20px !important; }
                    .customer-done-screen {
                        width: 100% !important;
                    }
                }
                @media (min-width: 1100px) {
                    .customer-photo-grid { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; gap: 8px !important; }
                    .customer-cart-content { grid-template-columns: 320px minmax(0, 1fr); gap: 28px; }
                    .customer-cart-grid { grid-template-columns: repeat(5, 1fr) !important; }
                }
                @media (min-width: 1440px) {
                    .customer-photo-grid { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
                    .customer-cart-grid { grid-template-columns: repeat(7, 1fr) !important; }
                }
            `}</style>
        </div>
        </div>
    );
}));

export default OrderSelect;
